import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { quizQuestions, calculateScores, getDiagnosis, getWeakCategories, type CategoryScores } from '@/components/quiz/quizData';
import { ArrowRight, ArrowLeft, Download, CheckCircle2, Zap, Eye, BarChart3, Shield, Target, Loader2 } from 'lucide-react';
import { downloadRoadmapPDF } from '@/components/quiz/generateRoadmapPDF';
import SEOHead from '@/components/SEOHead';

type Step = 'landing' | 'quiz' | 'capture' | 'loading' | 'results';

const categoryLabels: Record<string, { label: string; icon: React.ReactNode }> = {
  visibility: { label: 'Visibility', icon: <Eye className="w-4 h-4" /> },
  consistency: { label: 'Consistency', icon: <BarChart3 className="w-4 h-4" /> },
  authority: { label: 'Authority', icon: <Shield className="w-4 h-4" /> },
  leadGeneration: { label: 'Lead Generation', icon: <Target className="w-4 h-4" /> },
  conversion: { label: 'Conversion', icon: <Zap className="w-4 h-4" /> },
};

const Quiz = () => {
  const [step, setStep] = useState<Step>('landing');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [totalScore, setTotalScore] = useState(0);
  const [categoryScores, setCategoryScores] = useState<CategoryScores | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const question = quizQuestions[currentQ];
  const progress = ((currentQ) / quizQuestions.length) * 100;

  const handleSingleSelect = (option: string) => {
    setAnswers(prev => ({ ...prev, [question.id]: option }));
    // Auto-advance after short delay
    setTimeout(() => {
      if (currentQ < quizQuestions.length - 1) setCurrentQ(c => c + 1);
      else setStep('capture');
    }, 300);
  };

  const handleMultiToggle = (option: string) => {
    setAnswers(prev => {
      const current = (prev[question.id] as string[]) || [];
      const next = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
      return { ...prev, [question.id]: next };
    });
  };

  const handleTextChange = (value: string) => {
    setAnswers(prev => ({ ...prev, [question.id]: value }));
  };

  const canAdvance = () => {
    const a = answers[question.id];
    if (question.type === 'multi') return Array.isArray(a) && a.length > 0;
    if (question.type === 'text') return typeof a === 'string' && a.trim().length > 0;
    return !!a;
  };

  const advanceManually = () => {
    if (currentQ < quizQuestions.length - 1) setCurrentQ(c => c + 1);
    else setStep('capture');
  };

  const handleSubmitLead = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }
    setSubmitting(true);
    const { total, categories } = calculateScores(answers);
    setTotalScore(total);
    setCategoryScores(categories);

    try {
      const { error } = await supabase.from('quiz_leads' as any).insert({
        full_name: name,
        email,
        answers,
        total_score: total,
        category_scores: categories,
      } as any);
      if (error) throw error;

      // Also send email notification
      // Build readable quiz summary for email
      const questionMap: Record<string, string> = {
        goal: '90-Day Goal',
        posting_frequency: 'Posting Frequency',
        organic_leads: 'Organic Leads',
        bottleneck: 'Biggest Bottleneck',
        content_types: 'Content Types',
        clear_cta: 'Clear CTA',
        responsibility: 'Content Responsibility',
        service_area: 'Service Area',
        business_description: 'Business Description',
        monthly_revenue: 'Monthly Revenue',
      };

      const formattedAnswers = Object.entries(answers)
        .map(([key, val]) => {
          const label = questionMap[key] || key;
          const value = Array.isArray(val) ? val.join(', ') : val;
          return `<tr><td style="padding:8px 12px;border-bottom:1px solid #eee;font-weight:600;color:#555;white-space:nowrap;vertical-align:top;">${label}</td><td style="padding:8px 12px;border-bottom:1px solid #eee;color:#333;">${value}</td></tr>`;
        })
        .join('');

      const catLabels: Record<string, string> = {
        visibility: 'Visibility',
        consistency: 'Consistency',
        authority: 'Authority',
        leadGeneration: 'Lead Generation',
        conversion: 'Conversion',
      };

      const categoryRows = Object.entries(categories)
        .map(([key, val]) => {
          const pct = Math.round((val as number / 20) * 100);
          const color = pct >= 75 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
          return `<tr><td style="padding:6px 12px;font-weight:600;color:#555;">${catLabels[key] || key}</td><td style="padding:6px 12px;"><span style="color:${color};font-weight:700;">${val}/20</span></td></tr>`;
        })
        .join('');

      const scoreColor = total >= 75 ? '#22c55e' : total >= 50 ? '#f59e0b' : '#ef4444';

      const scoreSection = `
        <div style="text-align:center;padding:16px;margin:12px 0;background:#f9fafb;border-radius:8px;">
          <div style="font-size:14px;color:#666;margin-bottom:4px;">Overall Score</div>
          <div style="font-size:36px;font-weight:800;color:${scoreColor};">${total}<span style="font-size:18px;color:#999;">/100</span></div>
        </div>
        <table style="font-family:Arial,sans-serif;border-collapse:collapse;width:100%;max-width:500px;margin:0 auto 16px;">
          <tr><td colspan="2" style="padding:10px 12px;font-weight:700;color:#333;font-size:14px;border-bottom:2px solid #ddd;">Category Breakdown</td></tr>
          ${categoryRows}
        </table>
      `;

      const answersSection = `
        <table style="font-family:Arial,sans-serif;border-collapse:collapse;width:100%;max-width:500px;margin:0 auto;">
          <tr><td colspan="2" style="padding:10px 12px;font-weight:700;color:#333;font-size:14px;border-bottom:2px solid #ddd;">Quiz Responses</td></tr>
          ${formattedAnswers}
        </table>
      `;

      await supabase.functions.invoke('send-lead-notification', {
        body: {
          service_interest: 'Content System Quiz',
          full_name: name,
          email,
          biggest_priority: scoreSection,
          additional_info: answersSection,
        },
      });
    } catch (err) {
      console.error(err);
      // Still show results even if save fails
    }
    setSubmitting(false);
    setStep('loading');
  };

  // Loading animation
  useEffect(() => {
    if (step !== 'loading') return;
    const texts = [
      'Analyzing your content strategy…',
      'Identifying growth bottlenecks…',
      'Generating your custom roadmap…',
    ];
    let prog = 0;
    let textIdx = 0;
    setLoadingText(texts[0]);

    const interval = setInterval(() => {
      prog += 2;
      setLoadingProgress(prog);
      if (prog === 34) { textIdx = 1; setLoadingText(texts[1]); }
      if (prog === 68) { textIdx = 2; setLoadingText(texts[2]); }
      if (prog >= 100) {
        clearInterval(interval);
        setTimeout(() => setStep('results'), 400);
      }
    }, 40);
    return () => clearInterval(interval);
  }, [step]);

  const scoreColor = totalScore < 50 ? 'text-destructive' : totalScore <= 75 ? 'text-secondary' : 'text-primary';

  return (
    <>
      <SEOHead
        title="Free Content System Score Quiz | Content Marketing Assessment | Vantage Point Media"
        description="Take this free 60-second quiz to discover what's slowing your content growth. Get a personalized Content System Score and downloadable growth roadmap from Vantage Point Media in Columbus Ohio."
        canonical="https://vantagepointmediagroup.com/quiz"
        keywords="content marketing quiz, content system score, content strategy assessment, free marketing quiz Columbus Ohio"
        
      />
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">

          {/* LANDING */}
          {step === 'landing' && (
            <Card className="p-8 md:p-12 text-center animate-fade-in border-none shadow-medium">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Zap className="w-4 h-4" /> 60-Second Quiz
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
                Find Out What's Slowing Your Content Growth
              </h1>
              <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
                Get your Content System Score, biggest bottlenecks, and a custom growth roadmap.
              </p>
              <Button variant="hero" size="xl" onClick={() => setStep('quiz')} className="mx-auto">
                Start Quiz <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </Card>
          )}

          {/* QUIZ */}
          {step === 'quiz' && (
            <div className="animate-fade-in">
              <div className="mb-6">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>Question {currentQ + 1} of {quizQuestions.length}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              <Card className="p-6 md:p-10 border-none shadow-medium" key={question.id}>
                <h2 className="font-display text-xl md:text-2xl font-bold text-foreground mb-2">
                  {question.question}
                </h2>
                {question.microcopy && (
                  <p className="text-muted-foreground text-sm mb-6">{question.microcopy}</p>
                )}

                {question.type === 'single' && (
                  <div className="space-y-3">
                    {question.options!.map(opt => (
                      <button
                        key={opt}
                        onClick={() => handleSingleSelect(opt)}
                        className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-200 font-medium ${
                          answers[question.id] === opt
                            ? 'border-primary bg-primary/5 text-foreground'
                            : 'border-border bg-card hover:border-primary/40 text-foreground'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}

                {question.type === 'multi' && (
                  <>
                    <div className="space-y-3">
                      {question.options!.map(opt => {
                        const selected = ((answers[question.id] as string[]) || []).includes(opt);
                        return (
                          <button
                            key={opt}
                            onClick={() => handleMultiToggle(opt)}
                            className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-200 font-medium flex items-center gap-3 ${
                              selected
                                ? 'border-primary bg-primary/5 text-foreground'
                                : 'border-border bg-card hover:border-primary/40 text-foreground'
                            }`}
                          >
                            <CheckCircle2 className={`w-5 h-5 shrink-0 transition-colors ${selected ? 'text-primary' : 'text-muted-foreground/30'}`} />
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="default"
                      size="lg"
                      className="w-full mt-6"
                      disabled={!canAdvance()}
                      onClick={advanceManually}
                    >
                      Continue <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </>
                )}

                {question.type === 'text' && (
                  <>
                    <Textarea
                      value={(answers[question.id] as string) || ''}
                      onChange={e => handleTextChange(e.target.value)}
                      placeholder="e.g. We run a med spa helping women look and feel their best..."
                      rows={4}
                      maxLength={500}
                      className="bg-card border-border text-foreground"
                    />
                    <Button
                      variant="default"
                      size="lg"
                      className="w-full mt-6"
                      disabled={!canAdvance()}
                      onClick={advanceManually}
                    >
                      Continue <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </>
                )}

                <div className="flex justify-between mt-6">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => currentQ > 0 ? setCurrentQ(c => c - 1) : setStep('landing')}
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  <span className="text-xs text-muted-foreground self-center">
                    {quizQuestions.length - currentQ - 1} questions left
                  </span>
                </div>
              </Card>
            </div>
          )}

          {/* LEAD CAPTURE */}
          {step === 'capture' && (
            <Card className="p-8 md:p-12 text-center animate-fade-in border-none shadow-medium">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <CheckCircle2 className="w-4 h-4" /> Quiz Complete
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                Your Content System Score is Ready
              </h2>
              <p className="text-muted-foreground mb-8">
                Enter your details to unlock your personalized breakdown, growth gaps, and roadmap.
              </p>
              <div className="space-y-4 max-w-sm mx-auto text-left">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Name</label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-card border-border" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Email</label>
                  <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@company.com" className="bg-card border-border" />
                </div>
                <Button variant="hero" size="lg" className="w-full" onClick={handleSubmitLead} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'See My Results'} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <Button variant="ghost" size="sm" className="mt-4" onClick={() => { setCurrentQ(quizQuestions.length - 1); setStep('quiz'); }}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to quiz
              </Button>
            </Card>
          )}

          {/* LOADING */}
          {step === 'loading' && (
            <Card className="p-8 md:p-12 text-center animate-fade-in border-none shadow-medium">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-display text-xl font-bold text-foreground mb-6">{loadingText}</h2>
              <Progress value={loadingProgress} className="h-3 mb-2" />
              <p className="text-sm text-muted-foreground">{loadingProgress}%</p>
            </Card>
          )}

          {/* RESULTS */}
          {step === 'results' && categoryScores && (
            <div className="space-y-6 animate-fade-in">
              {/* Score */}
              <Card className="p-8 md:p-10 text-center border-none shadow-medium">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Your Content System Score</p>
                <p className={`font-display text-6xl md:text-7xl font-bold ${scoreColor}`}>
                  {totalScore}<span className="text-2xl text-muted-foreground"> / 100</span>
                </p>
                <p className="text-muted-foreground mt-4 max-w-md mx-auto">{getDiagnosis(totalScore)}</p>
              </Card>

              {/* Category Breakdown */}
              <Card className="p-6 md:p-8 border-none shadow-medium">
                <h3 className="font-display text-lg font-bold text-foreground mb-5">Category Breakdown</h3>
                <div className="space-y-4">
                  {(Object.entries(categoryScores) as [string, number][]).map(([key, value]) => {
                    const info = categoryLabels[key];
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                            {info.icon} {info.label}
                          </span>
                          <span className="text-sm font-bold text-foreground">{value}/20</span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-700"
                            style={{ width: `${(value / 20) * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Areas to Improve */}
              <Card className="p-6 md:p-8 border-none shadow-medium">
                <h3 className="font-display text-lg font-bold text-foreground mb-4">Areas to Improve</h3>
                <ul className="space-y-3">
                  {getWeakCategories(categoryScores).map((area, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                      <span className="text-foreground">{area}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Next Steps */}
              <Card className="p-6 md:p-8 border-none shadow-medium">
                <h3 className="font-display text-lg font-bold text-foreground mb-4">Recommended Next Steps</h3>
                <ul className="space-y-3">
                  {[
                    'Create 3–5 content pillars aligned with your ideal clients',
                    'Add clear CTAs to every post',
                    'Focus on trust-building content (case studies, behind-the-scenes)',
                    'Implement a weekly posting system with a content calendar',
                  ].map((s, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground">{s}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* CTA */}
              <Card className="p-8 md:p-10 text-center border-none shadow-medium bg-gradient-hero text-background">
                <h3 className="font-display text-2xl md:text-3xl font-bold mb-3">Want help implementing this?</h3>
                <p className="text-background/70 mb-6">Let us build a content system that turns views into revenue.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="hero"
                    size="lg"
                    onClick={() => window.open('https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1t6kd1aJ-Zaxjdd-7hbwwUZ87lIIZvYC_PDVWu_g46xzt2fdNBZDIbIvESPrJsHDbx0LJTrMJq', '_blank')}
                    className="bg-background text-foreground hover:bg-background/90"
                  >
                    Book a Free Strategy Call
                  </Button>
                  <Button
                    variant="heroOutline"
                    size="lg"
                    disabled={generatingPDF}
                    onClick={async () => {
                      setGeneratingPDF(true);
                      try {
                        await new Promise(r => setTimeout(r, 100));
                        downloadRoadmapPDF(
                          { fullName: name, email },
                          answers,
                          { total: totalScore, categories: categoryScores! }
                        );
                        toast.success('Roadmap downloaded!');
                      } catch (err) {
                        console.error(err);
                        toast.error('Failed to generate roadmap.');
                      } finally {
                        setGeneratingPDF(false);
                      }
                    }}
                  >
                    {generatingPDF ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating your roadmap…</>
                    ) : (
                      <><Download className="w-4 h-4 mr-1" /> Download Full Roadmap</>
                    )}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Quiz;
